import { FormEvent, useEffect, useMemo, useState } from 'react';
import { TableIconButton } from '../../components/TableIconAction';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, useTableSort } from '../../components/SortableTh';
import {
  Badge,
  Button,
  Callout,
  CheckIcon,
  CopyIcon,
  Label,
  ListEmptyState,
  LoadingSpinner,
  MailIcon,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  PencilIcon,
  RefreshIcon,
  SaveIcon,
  Select,
  Switch,
  TextInput,
  TrashIcon,
  UserPlusIcon,
  XIcon,
  useConfirm,
} from '../../components/ui';
import { ApiError, api, ManagedUser } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { t } from '../../i18n';
import { Office } from '../../types/office';

const userSortGetters: Record<
  string,
  (u: ManagedUser) => string | number | boolean | null | undefined
> = {
  name: (u) => u.name,
  email: (u) => u.email,
  role: (u) => u.role,
  office: (u) => u.office?.name,
  active: (u) => u.active,
};

export function UsersPage() {
  const labels = t().usersSettings;
  const confirm = useConfirm();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(true);
  const { sort, toggleSort } = useTableSort('name');
  const displayUsers = useMemo(
    () => sortItems(users, sort, userSortGetters),
    [users, sort],
  );
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'ADMIN' | 'USER'>('USER');
  const [officeId, setOfficeId] = useState('');
  const [active, setActive] = useState(true);
  const [formMode, setFormMode] = useState<'invite' | 'no-login'>('invite');
  const [canLogin, setCanLogin] = useState(true);
  const [credentialsUnlocked, setCredentialsUnlocked] = useState(false);
  const [resendingId, setResendingId] = useState<number | null>(null);
  const [inviteResult, setInviteResult] = useState<{
    inviteUrl: string;
    email: string;
    emailSent: boolean;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const roleOptions = [
    { value: 'USER', label: labels.user },
    { value: 'ADMIN', label: labels.admin },
  ];
  const officeOptions = offices
    .filter((item) => item.active || String(item.id) === officeId)
    .map((item) => ({
      value: String(item.id),
      label: item.active ? item.name : `${item.name} (${labels.inactive})`,
    }));

  async function load() {
    setLoading(true);
    setListError(null);
    try {
      const [userList, officeList] = await Promise.all([
        api.getUsers(),
        api.getOffices(false),
      ]);
      setUsers(userList);
      setOffices(officeList);
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function closeForm() {
    setFormOpen(false);
    setEditingId(null);
    setName('');
    setEmail('');
    setPassword('');
    setRole('USER');
    setOfficeId('');
    setActive(true);
    setFormMode('invite');
    setCanLogin(true);
    setCredentialsUnlocked(false);
    setFormError(null);
  }

  function openCreate() {
    setEditingId(null);
    setName('');
    setEmail('');
    setPassword('');
    setRole('USER');
    setOfficeId('');
    setActive(true);
    setFormMode('invite');
    setCanLogin(true);
    setCredentialsUnlocked(false);
    setFormError(null);
    setMessage(null);
    setFormOpen(true);
  }

  function openCreateWithoutLogin() {
    setEditingId(null);
    setName('');
    setEmail('');
    setPassword('');
    setRole('USER');
    setOfficeId('');
    setActive(true);
    setFormMode('no-login');
    setCanLogin(false);
    setCredentialsUnlocked(false);
    setFormError(null);
    setMessage(null);
    setFormOpen(true);
  }

  function openEdit(user: ManagedUser) {
    setEditingId(user.id);
    setName(user.name);
    setEmail(user.email ?? '');
    setPassword('');
    setRole(user.role);
    setOfficeId(user.officeId ? String(user.officeId) : '');
    setActive(user.active);
    setFormMode('invite');
    setCanLogin(user.canLogin !== false);
    setCredentialsUnlocked(true);
    setFormError(null);
    setMessage(null);
    setFormOpen(true);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setMessage(null);

    if (!name.trim()) {
      setFormError(labels.requiredName);
      return;
    }
    if (canLogin && !email.trim()) {
      setFormError(labels.requiredEmail);
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await api.updateUser(editingId, {
          name: name.trim(),
          ...(canLogin
            ? {
                email: email.trim(),
                role,
                ...(password ? { password } : {}),
              }
            : {}),
          officeId: officeId ? Number(officeId) : null,
          active,
        });
        setMessage(labels.saveSuccess);
        closeForm();
        await load();
      } else if (formMode === 'no-login') {
        await api.createUserWithoutLogin({
          name: name.trim(),
          officeId: officeId ? Number(officeId) : null,
        });
        setMessage(labels.saveSuccess);
        closeForm();
        await load();
      } else {
        const result = await api.inviteUser({
          name: name.trim(),
          email: email.trim(),
          role,
          officeId: officeId ? Number(officeId) : null,
        });
        closeForm();
        setInviteResult({
          inviteUrl: result.inviteUrl,
          email: result.user.email ?? '',
          emailSent: result.emailSent,
        });
        setCopied(false);
        setMessage(
          result.emailSent ? labels.inviteSent : labels.inviteCreatedNoEmail,
        );
        await load();
      }
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message : t().toasts.saveError,
      );
    } finally {
      setSaving(false);
    }
  }

  async function sendInviteToNoLogin() {
    if (!editingId) return;
    setFormError(null);
    setMessage(null);
    if (!name.trim()) {
      setFormError(labels.requiredName);
      return;
    }
    if (!email.trim()) {
      setFormError(labels.requiredEmail);
      return;
    }

    setSaving(true);
    try {
      await api.updateUser(editingId, {
        name: name.trim(),
        officeId: officeId ? Number(officeId) : null,
        active,
      });
      const result = await api.recreateUserInvite(editingId, {
        email: email.trim(),
        role,
      });
      closeForm();
      setInviteResult({
        inviteUrl: result.inviteUrl,
        email: result.user.email ?? email.trim(),
        emailSent: result.emailSent,
      });
      setCopied(false);
      setMessage(
        result.emailSent ? labels.inviteSent : labels.inviteCreatedNoEmail,
      );
      await load();
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message : t().toasts.saveError,
      );
    } finally {
      setSaving(false);
    }
  }

  async function resendInvite(user: ManagedUser) {
    setResendingId(user.id);
    setListError(null);
    try {
      const result = await api.recreateUserInvite(user.id);
      setInviteResult({
        inviteUrl: result.inviteUrl,
        email: result.user.email ?? '',
        emailSent: result.emailSent,
      });
      setCopied(false);
      setMessage(
        result.emailSent ? labels.inviteSent : labels.inviteCreatedNoEmail,
      );
      await load();
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setResendingId(null);
    }
  }

  async function deleteUser(user: ManagedUser) {
    const invitePending = user.canLogin !== false && Boolean(user.mustChangePassword);
    const ok = await confirm({
      title: invitePending ? labels.deleteInviteTitle : labels.deleteUserTitle,
      message: invitePending
        ? labels.deleteInviteMessage.replace('{email}', user.email ?? '')
        : labels.deleteUserMessage.replace('{name}', user.name),
      confirmText: labels.deleteInvite,
      cancelText: t().common.cancel,
      confirmVariant: 'danger',
    });
    if (!ok) return;

    setDeletingId(user.id);
    setListError(null);
    try {
      await api.deleteUser(user.id);
      setMessage(
        invitePending ? labels.deleteInviteSuccess : labels.deleteUserSuccess,
      );
      await load();
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setDeletingId(null);
    }
  }

  async function copyInviteLink() {
    if (!inviteResult) return;
    try {
      await navigator.clipboard.writeText(inviteResult.inviteUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <>
            <Button type="button" size="sm" variant="outline" onClick={openCreateWithoutLogin}>
              <UserPlusIcon className="h-3.5 w-3.5" />
              {labels.noLogin}
            </Button>
            <Button type="button" size="sm" onClick={openCreate}>
              <UserPlusIcon className="h-3.5 w-3.5" />
              {labels.invite}
            </Button>
          </>
        }
      />

      {listError ? (
        <Callout variant="danger" className="shrink-0">
          {listError}
        </Callout>
      ) : null}
      {message ? (
        <Callout variant="success" className="shrink-0">
          {message}
        </Callout>
      ) : null}

      <DataTablePanel>
        {loading ? (
          <LoadingSpinner message={t().common.loading} />
        ) : displayUsers.length === 0 ? (
          <ListEmptyState title={t().common.empty} />
        ) : (
          <table className="min-w-full text-sm">
            <thead className={dataTableHeadClass}>
              <tr>
                <th className="whitespace-nowrap px-3 py-2 font-medium">
                  {t().common.actions}
                </th>
                <SortableTh sortKey="name" sort={sort} onSort={toggleSort}>
                  {labels.name}
                </SortableTh>
                <SortableTh sortKey="email" sort={sort} onSort={toggleSort}>
                  {labels.email}
                </SortableTh>
                <SortableTh sortKey="role" sort={sort} onSort={toggleSort}>
                  {labels.role}
                </SortableTh>
                <SortableTh sortKey="office" sort={sort} onSort={toggleSort}>
                  {labels.office}
                </SortableTh>
                <th className="whitespace-nowrap px-3 py-2 font-medium">
                  {labels.inviteStatus}
                </th>
                <SortableTh sortKey="active" sort={sort} onSort={toggleSort}>
                  {t().customers.status}
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {displayUsers.map((user) => {
                const noLogin = user.canLogin === false;
                const invitePending = !noLogin && Boolean(user.mustChangePassword);
                return (
                  <tr key={user.id} className="border-t border-line">
                    <td className="whitespace-nowrap px-3 py-2">
                      <div className="flex items-center gap-1">
                        <TableIconButton
                          label={labels.edit}
                          onClick={() => openEdit(user)}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </TableIconButton>
                        {currentUser?.id !== user.id ? (
                          <TableIconButton
                            label={labels.deleteInvite}
                            onClick={() => void deleteUser(user)}
                            disabled={deletingId === user.id}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </TableIconButton>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-medium">{user.name}</td>
                    <td className="px-3 py-2">{user.email || '—'}</td>
                    <td className="px-3 py-2">
                      {noLogin
                        ? '—'
                        : user.role === 'ADMIN'
                          ? labels.admin
                          : labels.user}
                    </td>
                    <td className="px-3 py-2">{user.office?.name ?? '—'}</td>
                    <td className="px-3 py-2">
                      {noLogin ? (
                        <Badge>{labels.noLogin}</Badge>
                      ) : invitePending ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="warning">{labels.invitePending}</Badge>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            loading={resendingId === user.id}
                            onClick={() => void resendInvite(user)}
                          >
                            <RefreshIcon className="h-3.5 w-3.5" />
                            {labels.resendInvite}
                          </Button>
                        </div>
                      ) : (
                        <Badge variant="success">{labels.inviteAccepted}</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {user.active ? labels.active : labels.inactive}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </DataTablePanel>

      <Modal isOpen={formOpen} onClose={closeForm} size="md">
        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
          autoComplete="off"
        >
          <ModalHeader>
            <ModalTitle>
              {editingId
                ? labels.edit
                : formMode === 'no-login'
                  ? labels.noLoginTitle
                  : labels.inviteTitle}
            </ModalTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={closeForm}
              aria-label={t().common.cancel}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </ModalHeader>
          <ModalContent className="space-y-4">
            {formError ? <Callout variant="danger">{formError}</Callout> : null}
            {formMode === 'invite' && !editingId ? (
              <p className="text-sm text-muted">{labels.inviteHint}</p>
            ) : null}
            {formMode === 'no-login' && !editingId ? (
              <p className="text-sm text-muted">{labels.noLoginHint}</p>
            ) : null}
            {editingId && !canLogin ? (
              <p className="text-sm text-muted">{labels.inviteNoLoginHint}</p>
            ) : null}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -left-[9999px] h-0 w-0 overflow-hidden opacity-0"
            >
              <input
                type="text"
                name="username"
                autoComplete="username"
                tabIndex={-1}
              />
              <input
                type="password"
                name="password"
                autoComplete="current-password"
                tabIndex={-1}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label required>{labels.name}</Label>
                <TextInput
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  showClearButton={false}
                  placeholder=""
                  autoComplete="off"
                  name="bf-user-name"
                  autoFocus
                />
              </div>
              {canLogin || editingId ? (
                <div>
                  <Label required={canLogin}>{labels.email}</Label>
                  <TextInput
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setCredentialsUnlocked(true)}
                    showClearButton={false}
                    placeholder=""
                    autoComplete="off"
                    name="bf-user-email"
                    readOnly={Boolean(editingId) && !credentialsUnlocked}
                  />
                </div>
              ) : null}
              {editingId && canLogin ? (
                <div>
                  <Label>{labels.password}</Label>
                  <TextInput
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setCredentialsUnlocked(true)}
                    showClearButton={false}
                    placeholder={labels.passwordKeep}
                    autoComplete="new-password"
                    name="bf-user-password"
                    readOnly={!credentialsUnlocked}
                  />
                </div>
              ) : null}
              {canLogin || editingId ? (
                <div>
                  <Label>{labels.role}</Label>
                  <Select
                    value={role}
                    options={roleOptions}
                    showClearButton={false}
                    onChange={(e) =>
                      setRole(e.target.value as 'ADMIN' | 'USER')
                    }
                  />
                </div>
              ) : null}
              <div>
                <Label>{labels.office}</Label>
                <Select
                  value={officeId}
                  options={officeOptions}
                  placeholder={t().common.placeholders.select}
                  showClearButton
                  onChange={(e) => setOfficeId(e.target.value)}
                />
              </div>
            </div>
            {editingId || formMode === 'no-login' ? (
              <Switch
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                label={labels.active}
              />
            ) : null}
          </ModalContent>
          <ModalFooter>
            <Button type="button" variant="outline" onClick={closeForm}>
              <XIcon className="h-4 w-4" />
              {t().common.cancel}
            </Button>
            <Button type="submit" loading={saving}>
              {editingId || formMode === 'no-login' ? (
                <SaveIcon className="h-4 w-4" />
              ) : (
                <MailIcon className="h-4 w-4" />
              )}
              {editingId || formMode === 'no-login'
                ? t().common.save
                : labels.sendInvite}
            </Button>
            {editingId && !canLogin ? (
              <Button
                type="button"
                loading={saving}
                onClick={() => void sendInviteToNoLogin()}
              >
                <MailIcon className="h-4 w-4" />
                {labels.sendInvite}
              </Button>
            ) : null}
          </ModalFooter>
        </form>
      </Modal>

      <Modal
        isOpen={Boolean(inviteResult)}
        onClose={() => setInviteResult(null)}
        size="md"
      >
        <ModalHeader>
          <ModalTitle>{labels.inviteReadyTitle}</ModalTitle>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setInviteResult(null)}
            aria-label={t().common.cancel}
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </ModalHeader>
        <ModalContent className="space-y-4">
          {inviteResult?.emailSent ? (
            <Callout variant="success">{labels.inviteSent}</Callout>
          ) : (
            <Callout variant="warning">{labels.inviteCreatedNoEmail}</Callout>
          )}
          {inviteResult ? (
            <>
              <div className="rounded-lg border border-line bg-surface/50 px-3 py-3 text-sm">
                <span className="text-muted">{labels.email}: </span>
                <span className="font-medium">{inviteResult.email}</span>
              </div>
              <div>
                <Label>{labels.inviteLink}</Label>
                <TextInput
                  value={inviteResult.inviteUrl}
                  readOnly
                  showClearButton={false}
                />
              </div>
            </>
          ) : null}
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="outline" onClick={copyInviteLink}>
            <CopyIcon className="mr-1.5 h-4 w-4" />
            {copied ? labels.copied : labels.copyLink}
          </Button>
          <Button type="button" onClick={() => setInviteResult(null)}>
            <CheckIcon className="h-4 w-4" />
            {labels.closeInvite}
          </Button>
        </ModalFooter>
      </Modal>
    </PageShell>
  );
}
